import { useEffect, useRef, useState } from "react"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectSatelliteFlightById } from "features/entity-caching/flights/selectors"
import { useMapControl } from "features/map/control/useMapControl"
import { useViewport } from "features/map/GlobalViewState"
import {
    useSatelliteProvenanceData,
    type SatelliteSceneManifestEntry,
} from "./useSatelliteProvenanceData"
import { fetchTileImageData, lngLatToTileFraction } from "./provenanceTileSampling"
import { selectActiveCogOrtho } from "../layers/satelliteLayer"

const MAX_CACHED_TILES = 8

type CachedTile = {
    key: string
    imageData: ImageData
}

type CursorPosition = { x: number; y: number }

// Single shared scene-at-cursor lookup - do not reimplement per caller (e.g. point-in-polygon).
export function useSatelliteSceneAtCursor(flightId: string, enabled: boolean) {
    const flight = useAppSelector(selectSatelliteFlightById(flightId))
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const projectId = useAppSelector(selectActivatedFireId)
    const { unproject } = useMapControl()
    const viewport = useViewport()
    const { manifest, activeScenes } = useSatelliteProvenanceData(flightId)

    const [scene, setScene] = useState<SatelliteSceneManifestEntry>()
    const [cursor, setCursor] = useState<CursorPosition>()
    const tileCacheRef = useRef<CachedTile[]>([])
    const requestTokenRef = useRef(0)

    useEffect(() => {
        const cogOrtho = flight && selectActiveCogOrtho(flight)
        if (!enabled || !flight || !cogOrtho || !orgId || !projectId) return

        // contributedPixels is mosaic-scoped, not on the raw pixel-indexed scene list below.
        const activeSceneById = new Map((activeScenes ?? []).map((s) => [s.sceneId, s]))

        const handleMouseMove = async (e: MouseEvent) => {
            const token = ++requestTokenRef.current

            if ((e.target as HTMLElement)?.className !== "maplibregl-canvas") {
                setScene(undefined)
                setCursor(undefined)
                return
            }
            const [lng, lat] = unproject([e.offsetX, e.offsetY])
            const cursorPosition = { x: e.clientX, y: e.clientY }

            const { minLng, maxLng, minLat, maxLat } = cogOrtho
            if (lng < minLng || lng > maxLng || lat < minLat || lat > maxLat) {
                setScene(undefined)
                setCursor(undefined)
                return
            }

            const z = Math.max(
                cogOrtho.minZoom || 8,
                Math.min(cogOrtho.maxZoom || 21, Math.round(viewport.zoom))
            )
            const { x: xFrac, y: yFrac } = lngLatToTileFraction(lng, lat, z)
            const x = Math.floor(xFrac)
            const y = Math.floor(yFrac)
            // cloudFree is part of the key - it's a different index raster, not just a filter.
            const key = `${!!flight.cloudFree}/${z}/${x}/${y}`

            let cached = tileCacheRef.current.find((t) => t.key === key)
            if (!cached) {
                const imageData = await fetchTileImageData(
                    orgId,
                    projectId,
                    flightId,
                    !!flight.demoId,
                    !!flight.cloudFree,
                    z,
                    x,
                    y
                )
                if (token !== requestTokenRef.current) return // superseded by a later hover
                if (!imageData) {
                    setScene(undefined)
                    setCursor(undefined)
                    return
                }
                cached = { key, imageData }
                tileCacheRef.current = [cached, ...tileCacheRef.current].slice(0, MAX_CACHED_TILES)
            }

            const { width, height, data } = cached.imageData
            const pixelX = Math.min(width - 1, Math.max(0, Math.floor((xFrac - x) * width)))
            const pixelY = Math.min(height - 1, Math.max(0, Math.floor((yFrac - y) * height)))
            const offset = (pixelY * width + pixelX) * 4
            if (data[offset + 3] === 0) {
                setScene(undefined)
                setCursor(undefined)
                return
            }

            if (token !== requestTokenRef.current) return // superseded by a later hover
            const rawScene = manifest?.[data[offset]]
            setScene(rawScene && (activeSceneById.get(rawScene.sceneId) ?? rawScene))
            setCursor(cursorPosition)
        }

        document.addEventListener("mousemove", handleMouseMove)
        return () => {
            document.removeEventListener("mousemove", handleMouseMove)
        }
    }, [
        enabled,
        flight,
        orgId,
        projectId,
        flightId,
        manifest,
        activeScenes,
        unproject,
        viewport.zoom,
    ])

    return { scene, cursor }
}
